import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { StatusIndicator } from "components/partials/status-indicator/status-indicator";
import { IconButton } from "ui/icon-button";

import { Route as AppRoute } from "utilities/app-routes";
import { formatDueDate } from "utilities/date";

import { getBatchStatuses } from "containers/admin/clients/client/approval-queue/approval-queue.helpers";

import {
  BatchItem,
  BasicTableActionsProps,
} from "containers/admin/clients/client/approval-queue/approval-queue.types";
import { NamedDictionaryItem } from "hooks/api/dictionary";

import Icons from "assets";

interface TableActionsProps extends BasicTableActionsProps {
  batchStatuses: NamedDictionaryItem[];
}

const headerStyles = "text-gray-600 font-bold leading-5 pl-4";

/**
 * A header stays on one line while the column covers its label plus 32px of cell padding, and 28px
 * more where the column sorts (the arrow and the gap before it).
 */
export const createClientApprovalQueueColumns = ({
  pathname,
  batchStatuses,
}: TableActionsProps): ColumnDef<BatchItem>[] => [
  {
    id: "name",
    header: "Batch Name",
    accessorKey: "name",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="flex items-center gap-3 pr-3">
        <Link
          to={`${pathname}/${AppRoute.batch}/${row.original.id}`}
          className="text-aqua-600 hover:text-aqua-700 font-semibold"
          aria-disabled={false}
          tabIndex={undefined}>
          {row.original.name}
        </Link>
      </div>
    ),
    size: 500,
    meta: {
      headerClassName: headerStyles,
      className: "py-3",
    },
  },
  {
    id: "progress",
    header: "Progress",
    accessorFn: (row) => {
      const { approvedCount = 0, totalCount = 0 } = row;

      return totalCount > 0 ? (approvedCount / totalCount) * 100 : 0;
    },
    enableSorting: false,
    cell: ({ row }) => {
      const approvedCount = row.original.approvedCount;
      const totalCount = row.original.totalCount;

      return (
        <span className="text-gray-600 font-medium">
          {approvedCount}/{totalCount}
        </span>
      );
    },
    size: 100,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "status",
    enableSorting: true,
    cell: ({ row }) => {
      const statuses = getBatchStatuses(batchStatuses, row.original.status);

      return <StatusIndicator versionStatuses={statuses} />;
    },
    size: 115,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "dueDate",
    header: "Due date",
    accessorKey: "dueDate",
    enableSorting: true,
    cell: ({ row }) => (
      <div className="font-medium text-gray-600">{formatDueDate(row.original.dueDate)}</div>
    ),
    size: 135,
    meta: {
      headerClassName: headerStyles,
    },
  },
  {
    id: "action",
    header: "Action",
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex items-center justify-center gap-5">
        <IconButton
          to={`${pathname}/${AppRoute.batch}/${row.original.id}`}
          aria-label="View creative"
          data-testid="view-touchpoint-button"
          size="medium"
          variant="text">
          <Icons.EyeVisible />
        </IconButton>
      </div>
    ),
    size: 56,
    meta: {
      headerClassName: headerStyles,
      className: "pr-8",
      isHeaderCentered: true,
    },
  },
];
